import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ApiService, GraphQLResponse } from '@lsq-frontend/angular-shared/api-service';
import { ISettings, SETTINGS } from '@lsq-frontend/angular-shared/settings';
import { FeatureFlags } from '@lsq-frontend/fasttrack-shared/app-constants';
import { FeatureFlagService } from '@lsq-frontend/common-ui';
import { jsonToGraphQLQuery } from 'json-to-graphql-query';
import { startWith } from 'rxjs/operators';
import { TransfersInput, TransfersResponse } from '../../model/models';
import {
    GetPaymentsSummaryRequest,
    GetPaymentsSummaryResponse
} from '@lsq-frontend/remote-services/all-java/financing-mk2-services/buyer-account/account-summary/get-payments-summary';

@Injectable({ providedIn: 'root' })
export class GetPaymentsSummaryService extends ApiService<
    GetPaymentsSummaryRequest,
    GetPaymentsSummaryResponse
> {
    public requestType = GetPaymentsSummaryRequest;
    public responseType = GetPaymentsSummaryResponse;
    public endpointName = 'BuyerAccount/AccountSummary/GetPaymentsSummary';
    public serviceName = 'financing';

    private _isPayLaterEnabled = false;

    constructor(
        http: HttpClient,
        @Inject(SETTINGS) settings: ISettings,
        private _featureFlagService: FeatureFlagService
    ) {
        super(http, settings);

        this._featureFlagService
            .isOn(FeatureFlags.PayLater)
            .pipe(startWith(this._featureFlagService.isOnSync(FeatureFlags.PayLater)))
            .subscribe((isOn) => {
                this._isPayLaterEnabled = isOn;
                if (isOn) {
                    this.endpointName = 'v1/query';
                    this.serviceName = 'financingGql';
                    this.usePascalForUrl = false;
                } else {
                    this.endpointName = 'BuyerAccount/AccountSummary/GetPaymentsSummary';
                    this.serviceName = 'financing';
                    this.usePascalForUrl = true;
                }
            });
    }

    public requestMapper(
        req: GetPaymentsSummaryRequest
    ): { query: string } | GetPaymentsSummaryRequest {
        if (this._isPayLaterEnabled) {
            const input: TransfersInput = {};
            const query = jsonToGraphQLQuery({
                transfers: {
                    __args: { input },
                    totals: {
                        totalAmountNotYetDue: {
                            cents: true,
                            currencyCode: true
                        },
                        totalAmountDue: {
                            cents: true,
                            currencyCode: true
                        },
                        totalAmountOpen: {
                            cents: true,
                            currencyCode: true
                        }
                    }
                }
            });
            return { query: `{${query}}` };
        }
        return req;
    }

    public get endpointUrl(): string {
        return this._isPayLaterEnabled ? super.endpointUrl.replace('/api', '') : super.endpointUrl;
    }

    public responseMapper(
        res: GraphQLResponse<TransfersResponse> | GetPaymentsSummaryResponse
    ): GetPaymentsSummaryResponse {
        if (this._isPayLaterEnabled) {
            const { transfers } = (res as GraphQLResponse<TransfersResponse>).data;
            return new GetPaymentsSummaryResponse({
                paymentsSummary: {
                    // TODO: non-TPM buyers might need to read `fundingAmountDue` instead of `totalAmountDue`.
                    currentBalance: transfers.totals.totalAmountDue.cents / 100,
                    totalBalance: transfers.totals.totalAmountOpen.cents / 100,
                    remainingBalance: transfers.totals.totalAmountNotYetDue.cents / 100
                }
            });
        }
        return res as GetPaymentsSummaryResponse;
    }
}
